import { BuildingMenuType } from '@/enums';
import { LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import type { BuildingCategory, BuildingInStoreInfo, ContentsPropertyManagement, FloorInfo, ManagedPoiContent, PoiContent } from '@/services/dto';
import type { PoiContentSlide } from '@/types/shop';
import { disassembleCompleteCharacter } from 'es-hangul';
import { extractConsonants } from './hangul/extractConsonants';
import { sortKoEnFirst } from './sort/ko-en-first/sortKoEnFirst';

export const parseFloorLabel = (label: string): number => {
    const match = label.match(/(B?)(\d+)/i);
    return match ? (match[1].toUpperCase() === 'B' ? -parseInt(match[2], 10) : parseInt(match[2], 10)) : 0;
};

const extractChosung = (str: string): string => {
    let result = '';
    for (const char of str) {
        const disassembled = disassembleCompleteCharacter(char);
        if (disassembled) {
            // Only take choseong (first consonant), not all consonants
            result += disassembled.choseong;
        } else {
            // For non-Hangul characters, keep as is
            result += char;
        }
    }
    return result;
};

/**
 * @desc 매장 검색에서 매장 필터링 시 사용하는 로직 (chosung 기반 검색)
 * @param {string} targetText 검색 대상 텍스트
 * @param {string} searchText 검색어
 * @returns {boolean} 검색 결과 - 모음만 입력시 false 반환
 */
export const searchByText = (targetText: string, searchText: string): boolean => {
    if (!searchText.trim()) return true;

    const target = targetText.toUpperCase();
    const search = searchText.toUpperCase();

    // Non-Hangul queries: simple substring match
    if (!/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(search)) {
        return target.includes(search);
    }

    // Korean search logic
    // Check if searchText contains only chosung characters
    const isSearchOnlyChosung = /^[ㄱ-ㅎ]+$/.test(search);

    const targetChosung = isSearchOnlyChosung ? extractChosung(target) : target;
    return targetChosung.includes(search);
};

/**
 * @desc 검색 결과를 정렬하는 함수 (한국어 초성 우선 정렬)
 * @param {Array} items 정렬할 아이템 배열
 * @param {string} searchText 검색어
 * @param {string} nameField 이름 필드명
 * @param {string} lang 언어 코드
 * @returns {Array} 정렬된 아이템 배열
 */
export const sortSearchResults = <T extends Record<string, unknown>>(items: T[], searchText: string, nameField: keyof T, lang: string = 'ko'): T[] => {
    if (!searchText.trim()) {
        return items.sort((a: T, b: T) => {
            const nameA = (a[nameField] as Record<string, string>)?.[lang] || '';
            const nameB = (b[nameField] as Record<string, string>)?.[lang] || '';
            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        });
    }

    const search = searchText.toUpperCase();
    const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(search);

    return items.sort((a: T, b: T): number => {
        const nameA = (a[nameField] as Record<string, string>)?.[lang] || '';
        const nameB = (b[nameField] as Record<string, string>)?.[lang] || '';

        if (isKorean) {
            // 한국어인 경우 초성으로 시작하는지 확인
            const disassembleString = (str: string): string => {
                let result = '';
                for (const char of str) {
                    const disassembled = disassembleCompleteCharacter(char);
                    if (disassembled) {
                        result += disassembled.choseong + disassembled.jungseong + disassembled.jongseong;
                    } else {
                        result += char;
                    }
                }
                return result;
            };

            const startsWithChosungA = extractConsonants(disassembleString(nameA)).startsWith(search);
            const startsWithChosungB = extractConsonants(disassembleString(nameB)).startsWith(search);

            // 검색어가 포함된 아이템들을 오름차순 정렬
            if (startsWithChosungA && startsWithChosungB) {
                return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
            }

            if (startsWithChosungA) return -1;
            if (startsWithChosungB) return 1;

            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        } else {
            // 한국어가 아닌 경우 일반 문자열 검색
            const startsWithSearchWordA = nameA.toUpperCase().startsWith(search);
            const startsWithSearchWordB = nameB.toUpperCase().startsWith(search);

            if (startsWithSearchWordA && startsWithSearchWordB) {
                return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
            }

            if (startsWithSearchWordA) return -1;
            if (startsWithSearchWordB) return 1;

            return sortKoEnFirst(nameA.toUpperCase(), nameB.toUpperCase());
        }
    });
};

export const getFloorName = (lang: string, studioFloorId: string, floors: FloorInfo[], fallbackLang = 'ko') => {
    const floorInfo = floors.find((floor) => floor.floor_code === studioFloorId);
    return floorInfo?.name[lang] || floorInfo?.name[fallbackLang] || '';
};

export const getBuildingLocalizedName = (building: BuildingInStoreInfo | undefined, lang: string, fallbackLang = 'ko'): string => {
    if (!building?.name) return '';
    return building.name[lang] || building.name[fallbackLang] || '';
};

export const getCategoryLocalizedName = (category: BuildingCategory, lang: string, fallbackLang = 'ko'): string => {
    const { name } = category;
    if (!name) return '';
    if (typeof name === 'string') return name;
    if (Array.isArray(name)) {
        return (
            (name as { code: string; name: string }[]).find((item) => item.code === lang)?.name ??
            (name as { code: string; name: string }[]).find((item) => item.code === fallbackLang)?.name ??
            ''
        );
    }
    return name[lang] || name[fallbackLang] || '';
};

export const getPoiCategoryCodes = (poi: PoiContent): string[] => poi.categories ?? poi.rootCategories ?? [];

const getSearchMenuSelectedCategories = (building: BuildingInStoreInfo): BuildingCategory[] =>
    building.buildingMenus?.find((menu) => menu.menu_type === BuildingMenuType.SEARCH)?.selectedCategories ?? [];

export const getBuildingNameByStudioId = (lang: string, studioBuildingId: string, buildings: BuildingInStoreInfo[]) => {
    const building = buildings.find((building) => building.studioBuildingId === studioBuildingId);
    return getBuildingLocalizedName(building, lang);
};

export const getBuildingNameByBuildingId = (lang: string, buildingId: string, buildings: BuildingInStoreInfo[]) => {
    const building = buildings.find((building) => building.id === buildingId);
    return getBuildingLocalizedName(building, lang);
};

const buildManagedPoiContents = (poiContents: PoiContent['contents'], propertyManagement: ContentsPropertyManagement[]): ManagedPoiContent[] =>
    propertyManagement.map((item) => ({
        field_key: item.field_key,
        name: item.content,
        attribute_type: item.attribute_type,
        display_type: item.display_type,
        content: poiContents[item.field_key as keyof typeof poiContents],
    }));

export const getSortedContents = (contents: PoiContentSlide[], language: string): PoiContentSlide[] => {
    return contents.sort((a, b) => {
        const nameA = a.data.name[language] || '';
        const nameB = b.data.name[language] || '';
        return nameA.localeCompare(nameB, language, {
            numeric: false,
            sensitivity: 'base',
            ignorePunctuation: true,
        });
    });
};

export const filterPoiContents = (building: BuildingInStoreInfo, poiContents: PoiContent[]) => {
    const poiContentsByBuilding = poiContents?.filter((poi) => poi.studioBuildingId === building.studioBuildingId);
    const shopPoiContents: PoiContent[] = [];
    const fnbPoiContents: PoiContent[] = [];
    const facilityPoiContents: PoiContent[] = [];
    const otherUtilityPoiContents: PoiContent[] = [];

    const rootShopCategories = building.buildingCategoriesSelected ?? [];

    const contentsPropertyManagement = (building.contentsPropertyManagement ?? [])
        .filter((property) => property.visible)
        .sort((a, b) => a.sort_order - b.sort_order);

    poiContentsByBuilding?.forEach((poi: PoiContent) => {
        const categoriesSet = new Set(getPoiCategoryCodes(poi));

        const isShopContent = rootShopCategories.some((category) => categoriesSet.has(category.categoryCode));

        if (isShopContent) {
            shopPoiContents.push({
                ...poi,
                managedContents: buildManagedPoiContents(poi.contents, contentsPropertyManagement),
            });
        }
    });

    return {
        shopPoiContents,
        fnbPoiContents,
        facilityPoiContents,
        otherUtilityPoiContents,
    };
};

type MergedCategory = BuildingCategory & {
    mergeCategoryCode: string[];
};

const getCategoryCodesFromItem = (item: BuildingCategory): string[] => {
    const mergeCodes = (item as MergedCategory).mergeCategoryCode?.filter(Boolean) ?? [];
    if (mergeCodes.length > 0) {
        return mergeCodes;
    }
    return item.categoryCode ? [item.categoryCode] : [];
};

export const mergeCategoriesByClientCode = (categories: BuildingCategory[]): MergedCategory[] => {
    if (!categories || categories.length === 0) return [];

    const mergedMap = new Map<string, MergedCategory>();

    categories.forEach((item) => {
        if (item.clientCode === 'all') return;

        const { clientCode } = item;
        const codesToAdd = getCategoryCodesFromItem(item);

        if (!codesToAdd.length) return;

        if (!mergedMap.has(clientCode)) {
            const { categoryCode: _, mergeCategoryCode: __, ...rest } = item as MergedCategory;

            mergedMap.set(clientCode, {
                ...rest,
                categoryCode: '',
                mergeCategoryCode: [...codesToAdd],
            });
        } else {
            const existingItem = mergedMap.get(clientCode)!;

            codesToAdd.forEach((code) => {
                if (!existingItem.mergeCategoryCode.includes(code)) {
                    existingItem.mergeCategoryCode.push(code);
                }
            });
        }
    });

    return Array.from(mergedMap.values());
};

const filterCategories = (building: BuildingInStoreInfo, poiContents: PoiContent[]) => {
    const fnbMap = new Map<string, BuildingCategory>();
    const facilityMap = new Map<string, BuildingCategory>();
    const otherFacilityMap = new Map<string, BuildingCategory>();

    const result = {
        shopRootCategory: null as BuildingCategory[] | null,
        fnbRootCategory: null as BuildingCategory | null,
        facilityRootCategory: null as BuildingCategory | null,
        otherFacilityRootCategory: null as BuildingCategory | null,
    };

    const categoriesInSearch = getSearchMenuSelectedCategories(building);

    result.shopRootCategory = categoriesInSearch;

    const mergedShopCategories = mergeCategoriesByClientCode(building.buildingCategoriesSelected ?? []);

    return {
        ...result,
        shopCategories: [...mergedShopCategories],
        fnbCategories: [...fnbMap.values()],
        facilityCategories: [...facilityMap.values()],
        otherFacilityCategories: [...otherFacilityMap.values()],
    };
};

export const extractMatchingKeyword = (crltSrchKwrd: Record<string, string>, searchText: string, language: string): string | undefined => {
    if (!searchText?.trim()) return undefined;

    const normalizedSearchText = searchText.trim();

    const hashTags = crltSrchKwrd[language] || '';
    const keywords = hashTags.split(',').map((k) => k.trim());

    for (const keyword of keywords) {
        if (keyword && searchByText(keyword, normalizedSearchText)) {
            return keyword;
        }
    }

    const keywordDefault = crltSrchKwrd['ko'] || '';
    const keywordsDefault = keywordDefault.split(',').map((k) => k.trim());

    for (const keyword of keywordsDefault) {
        if (keyword && searchByText(keyword, normalizedSearchText)) {
            return keyword;
        }
    }

    return undefined;
};

const poiUtils = {
    parseFloorLabel,
    searchByText,
    sortSearchResults,
    getFloorName,
    getBuildingLocalizedName,
    getPoiCategoryCodes,
    getBuildingNameByStudioId,
    getBuildingNameByBuildingId,
    getSortedContents,
    filterPoiContents,
    filterCategories,
    extractMatchingKeyword,
};

export default poiUtils;
